import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'sonner';
import { siteApi } from '@/api/site';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useStudioStore } from '@/store/useStudioStore';
import { AUTOPILOT_CLONE_FLOW_QUERY_KEY, isCloneGeneratedProject } from '@/hooks/autopilot/shared';
import {
  normalizeLegacyPlatformUrl,
  normalizeLegacyPlatformUrlWithTrailingSlash,
} from '@/lib/platformDomain';
import {
  AUTOPILOT_CLONE_DIALOG_QUERY_KEY,
  ROUTE_ENTRY_QUERY_KEY,
  ROUTE_ENTRY_VALUES,
} from '@/lib/routeParams';
import { trackStudioEvent } from '@/lib/studioTracking';
import { openCreditPurchase } from '@/lib/creditPurchase';

export const StudioPublishSuccessModal = () => {
  const { t } = useTranslation('studio');
  const {
    isPublishSuccessModalOpen,
    setPublishSuccessModalOpen,
    currentName,
    lastPublishedUrl,
    setSettingsOpen,
    setActiveSettingsTab,
    currentProject,
    currentSiteId,
    currentSiteInfo,
    setCurrentSiteInfo,
    isGadsTagConnected,
    creditBalance,
    creditBalanceLoading,
  } = useStudioStore();

  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const sitePath = currentProject?.sitePaths?.[0];
  const effectiveSiteId = currentSiteId ?? (sitePath?.siteId ? String(sitePath.siteId) : null);
  const matchedSiteInfo =
    currentSiteInfo && effectiveSiteId && currentSiteInfo.id === Number(effectiveSiteId)
      ? currentSiteInfo
      : null;

  const normalizedUrl = lastPublishedUrl
    ? normalizeLegacyPlatformUrl(
        lastPublishedUrl.replace(/^(https?:\/\/)+/, 'https://').replace(/([^:])\/\/+/g, '$1/'),
      )
    : null;
  const displayUrl = normalizedUrl
    ? normalizeLegacyPlatformUrlWithTrailingSlash(normalizedUrl)
    : '';
  const isDomainDnsVerified =
    matchedSiteInfo?.status === 2 || matchedSiteInfo?.sslCertStatus === 'ACTIVE';
  const isCreditBlocked =
    creditBalanceLoading || (typeof creditBalance === 'number' && creditBalance <= 0);

  useEffect(() => {
    if (!isPublishSuccessModalOpen || !effectiveSiteId || matchedSiteInfo) return;

    let cancelled = false;

    const loadSiteInfo = async () => {
      try {
        const res = await siteApi.getSiteList({ pageNumber: 1, pageSize: 500 });
        const site = res.list.find((item) => item.id === Number(effectiveSiteId)) ?? null;
        if (!cancelled) {
          setCurrentSiteInfo(site);
        }
      } catch {
        // Keep the CTA visible when the domain verification state cannot be confirmed.
      }
    };

    loadSiteInfo();

    return () => {
      cancelled = true;
    };
  }, [effectiveSiteId, isPublishSuccessModalOpen, matchedSiteInfo, setCurrentSiteInfo]);

  const handleCopy = () => {
    if (!normalizedUrl) return;
    navigator.clipboard.writeText(normalizedUrl);
    setCopied(true);
    toast.success(t('publish.success.url_copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const openSettings = (tab: string) => {
    setActiveSettingsTab(tab);
    trackStudioEvent('studio_settings_open', {
      entrySource: 'publish_success',
      defaultTab: tab,
    });
    setSettingsOpen(true);
    setPublishSuccessModalOpen(false);
  };

  const handleOpenAutopilot = () => {
    setPublishSuccessModalOpen(false);
    const autopilotSearchParams = new URLSearchParams();
    if (effectiveSiteId) {
      autopilotSearchParams.set('siteId', String(effectiveSiteId));
    }
    if (isCloneGeneratedProject(currentProject)) {
      autopilotSearchParams.set(ROUTE_ENTRY_QUERY_KEY, ROUTE_ENTRY_VALUES.cloneMigration);
      autopilotSearchParams.set(AUTOPILOT_CLONE_FLOW_QUERY_KEY, '1');
      autopilotSearchParams.set(AUTOPILOT_CLONE_DIALOG_QUERY_KEY, '1');
    } else {
      autopilotSearchParams.set(ROUTE_ENTRY_QUERY_KEY, ROUTE_ENTRY_VALUES.autopilotWorkspace);
    }
    navigate(`/autopilot?${autopilotSearchParams.toString()}`);
  };

  return (
    <Dialog open={isPublishSuccessModalOpen} onOpenChange={setPublishSuccessModalOpen}>
      <DialogContent
        className="pp-modal w-[94%] overflow-hidden rounded-[18px] border-none p-0 shadow-[0_20px_56px_rgba(0,0,0,0.12),0_1px_0_rgba(0,0,0,0.05)] focus:outline-none focus-visible:outline-none sm:max-w-[640px]"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{t('publish.success.title')}</DialogTitle>
        <div className="pp-modal-inner relative min-w-0">
          <div className="absolute top-0 right-0 left-0 h-[3px] bg-[linear-gradient(135deg,#6D28D9_0%,#0891B2_100%)]" />

          <div className="pp-hero min-w-0 overflow-hidden p-[28px_26px_22px]">
            <div className="pp-hero-status mb-[14px] flex items-center gap-[6px] text-[11.5px] font-[700] text-[#059669]">
              <span className="pp-hero-status-dot h-[7px] w-[7px] animate-[ppLivePulse_2s_ease-in-out_infinite] rounded-full bg-[#059669]" />
              Your page is live
            </div>
            <div className="pp-hero-name mb-[16px] text-[23px] leading-[1.15] font-[900] tracking-[-0.55px] text-[#0D0D1A]">
              {currentName}
            </div>
            <p className="mb-[18px] max-w-[520px] text-[14px] leading-[1.6] text-[#64748B]">
              Your page is live and ready to help more people find you, understand your offer, and
              take action.
            </p>
            <div className="mb-[7px] text-[11px] font-bold tracking-[0.02em] text-[#94A3B8] uppercase">
              Live page URL
            </div>
            <div className="pp-url-row border-border flex w-full min-w-0 items-stretch overflow-hidden rounded-[10px] border bg-[#F7F7FC]">
              <span className="pp-url-text min-w-0 flex-1 self-center overflow-hidden p-[10px_13px] font-mono text-[12.5px] font-[600] text-ellipsis whitespace-nowrap text-[#0D0D1A]">
                {displayUrl}
              </span>
              <div className="pp-url-sep w-[1px] shrink-0 bg-black/7" />
              <button
                onClick={handleCopy}
                disabled={!normalizedUrl}
                className={`pp-url-btn flex w-[38px] shrink-0 items-center justify-center bg-transparent text-[12px] transition-all hover:bg-[#6D28D9]/[0.06] ${copied ? 'text-[#059669]' : 'text-[#0D0D1A]/38 hover:text-[#6D28D9]'}`}
                title={t('publish.copy_url')}
              >
                <Icon icon="fa6-solid:copy" className="text-[12px]" />
              </button>
              <div className="pp-url-sep w-[1px] shrink-0 bg-black/7" />
              <button
                onClick={() => normalizedUrl && window.open(displayUrl, '_blank')}
                disabled={!normalizedUrl}
                className="pp-url-btn flex w-[38px] shrink-0 items-center justify-center bg-transparent text-[12px] text-[#0D0D1A]/38 transition-all hover:bg-[#6D28D9]/[0.06] hover:text-[#6D28D9]"
                title={t('publish.success.open_page')}
              >
                <Icon icon="fa6-solid:arrow-up-right-from-square" className="text-[12px]" />
              </button>
            </div>
          </div>

          <div className="pp-sep h-[1px] bg-black/7" />

          <div className="grid grid-cols-1 gap-3 bg-[#F8FAFC]/75 px-[26px] py-[18px] sm:grid-cols-3">
            <div className="flex min-w-0 items-start gap-2">
              <Icon
                icon="fa6-solid:bullseye"
                className="mt-0.5 shrink-0 text-[12px] text-[#059669]"
              />
              <div>
                <strong className="block text-[12px] leading-[1.35] text-[#0D0D1A]">
                  Reach the right people
                </strong>
                <span className="mt-1 block text-[11px] leading-[1.4] text-[#94A3B8]">
                  Give new visitors a clear reason to stay.
                </span>
              </div>
            </div>
            <div className="flex min-w-0 items-start gap-2">
              <Icon
                icon="fa6-solid:arrow-trend-up"
                className="mt-0.5 shrink-0 text-[12px] text-[#059669]"
              />
              <div>
                <strong className="block text-[12px] leading-[1.35] text-[#0D0D1A]">
                  Turn visits into action
                </strong>
                <span className="mt-1 block text-[11px] leading-[1.4] text-[#94A3B8]">
                  Make it easier for interest to become a response.
                </span>
              </div>
            </div>
            <div className="flex min-w-0 items-start gap-2">
              <Icon
                icon="fa6-solid:chart-line"
                className="mt-0.5 shrink-0 text-[12px] text-[#059669]"
              />
              <div>
                <strong className="block text-[12px] leading-[1.35] text-[#0D0D1A]">
                  Build on what works
                </strong>
                <span className="mt-1 block text-[11px] leading-[1.4] text-[#94A3B8]">
                  Use real visitor signals to improve over time.
                </span>
              </div>
            </div>
          </div>

          <div className="pp-sep h-[1px] bg-black/7" />

          {isCreditBlocked ? (
            <div className="mx-5 mt-[14px] flex flex-wrap items-center gap-3 rounded-[11px] border border-[rgba(220,38,38,0.2)] bg-[linear-gradient(135deg,rgba(254,226,226,0.76),rgba(255,247,237,0.88))] p-[13px_14px] sm:flex-nowrap">
              <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[#DC2626]/10 text-[#B91C1C]">
                <Icon icon="fa6-solid:lock" className="text-[13px]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-extrabold text-[#0D0D1A]">
                  Ready to keep improving your page?
                </div>
                <div className="mt-[3px] text-[11.5px] leading-[1.45] text-[#64748B]">
                  Your credit balance is below zero. Add credits to continue making changes in
                  Studio.
                </div>
              </div>
              <button
                type="button"
                onClick={openCreditPurchase}
                className="inline-flex h-[32px] shrink-0 items-center gap-1.5 rounded-[8px] bg-[#B91C1C] px-3 text-[11.5px] font-bold text-white transition-colors hover:bg-[#991B1B]"
              >
                <Icon icon="fa6-solid:coins" className="text-[11px]" />
                Buy credits
              </button>
            </div>
          ) : null}

          <div className="px-[26px] pt-[6px] pb-[4px]">
            <div className="py-[12px] text-[11px] font-bold tracking-[0.03em] text-[#94A3B8] uppercase">
              Ways to grow from here
            </div>
            <button
              type="button"
              className="flex w-full items-center gap-3 border-b border-black/7 py-[11px] text-left transition-opacity hover:opacity-70"
              onClick={() => openSettings('domain')}
            >
              <span
                className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] text-[12px] ${
                  isDomainDnsVerified
                    ? 'bg-[#059669]/10 text-[#059669]'
                    : 'bg-[#D97706]/10 text-[#D97706]'
                }`}
              >
                <Icon icon={isDomainDnsVerified ? 'fa6-solid:check' : 'fa6-solid:globe'} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[#0D0D1A]">
                  Make it easier to trust and share
                </span>
                <span className="mt-[3px] block text-[11.5px] leading-[1.4] text-[#94A3B8]">
                  Use your own web address when you want the page to feel fully yours.
                </span>
              </span>
              <span
                className={`shrink-0 text-[12px] font-bold ${
                  isDomainDnsVerified ? 'text-[#059669]' : 'text-[#6D28D9]'
                }`}
              >
                {isDomainDnsVerified ? 'Connected' : 'Connect'}
              </span>
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 py-[11px] text-left transition-opacity hover:opacity-70"
              onClick={() => openSettings('gads')}
            >
              <span
                className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] text-[12px] ${
                  isGadsTagConnected
                    ? 'bg-[#059669]/10 text-[#059669]'
                    : 'bg-[#D97706]/10 text-[#D97706]'
                }`}
              >
                <Icon icon={isGadsTagConnected ? 'fa6-solid:check' : 'fa6-solid:chart-line'} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[#0D0D1A]">
                  See where interest turns into action
                </span>
                <span className="mt-[3px] block text-[11.5px] leading-[1.4] text-[#94A3B8]">
                  Measure visitor activity so you can focus your next improvement.
                </span>
              </span>
              <span
                className={`shrink-0 text-[12px] font-bold ${
                  isGadsTagConnected ? 'text-[#059669]' : 'text-[#6D28D9]'
                }`}
              >
                {isGadsTagConnected ? 'Tracking' : 'Track'}
              </span>
            </button>
          </div>

          <div className="pp-footer p-[10px_26px_20px]">
            <div className="flex w-full flex-col-reverse items-stretch justify-end gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleOpenAutopilot}
                className="inline-flex h-[38px] items-center justify-center gap-2 rounded-[8px] border border-black/10 bg-white px-4 text-[13px] font-bold text-[#64748B] transition-colors hover:border-[#6D28D9]/30 hover:bg-[#6D28D9]/[0.04] hover:text-[#6D28D9]"
              >
                <Icon icon="fa6-solid:robot" className="text-[13px]" />
                Explore Autopilot
              </button>
              <button
                onClick={() => setPublishSuccessModalOpen(false)}
                className="inline-flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-[8px] border-none bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-4 font-sans text-[13px] font-bold text-white shadow-[0_5px_14px_rgba(109,40,217,0.2)] transition-opacity hover:opacity-90"
              >
                Continue in Studio
                <Icon icon="fa6-solid:arrow-right" className="text-[12px]" />
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
